import { SessionDb } from 'lib/cosmos/session/sessionDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { pluralHelper } from 'lib/helpers/plural.helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { map, of } from 'rxjs';
import { ListWorkoutsComponent } from 'src/app/modules/workouts/list-workouts.component.js';
import { ViewOrEditWorkoutComponent } from 'src/app/modules/workouts/view-or-edit-workout.component.js';
import { WorkoutFormComponent } from 'src/app/modules/workouts/workout-form.component.js';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import {
	ICoreRoutes,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypeParentRoute,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IWorkoutListRouteData {
	workoutType: WorkoutDb.WorkoutType;
}

interface IClientData {
	client: UserDb.IUserItem;
	trainingContainer: UserDb.IUserTrainingContainer;
	trainingItem: UserDb.IUserTrainingItem;
}

export interface IViewOrEditWorkoutPlanRouteData {
	workout: WorkoutDb.IWorkoutItem | undefined;
	sessionForDate: SessionDb.ISessionItem | undefined;
	isWorkoutDesigner: boolean;
	workoutType: WorkoutDb.WorkoutType;
	clientData: IClientData | undefined;
}

export interface IWorkoutRoutes extends ICoreRoutes {
	workouts: ITypeRoute<
		undefined,
		{
			custom: ITypeRouteAction<IWorkoutListRouteData>;
			new: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	activities: ITypeRoute<
		undefined,
		{
			custom: ITypeRouteAction<IWorkoutListRouteData>;
			new: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	workout: ITypeRoute<
		IViewOrEditWorkoutPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
			edit: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
			design: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	activity: ITypeRoute<
		IViewOrEditWorkoutPlanRouteData,
		{
			edit: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
}

export const workoutRoutes: RouteInitializer<IWorkoutRoutes> = {
	workouts: {
		mainPath: 'trainer',
		uniqueId: 'workouts',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			custom: {
				uniqueId: `customWorkouts`,
				component: ListWorkoutsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `My workouts`
					};
					return metadata;
				},
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IWorkoutListRouteData> = {
						linkedItems: [],
						data: {
							workoutType: 'workout'
						}
					};

					return sharedData;
				}
			},
			new: {
				parentRoutes: (context) => [
					{
						routerLink: context.dependencies.navigateService
							.actionRoute((routes) => routes.workoutRoutes.workouts.children.custom)
							.getUrl(),
						title: `Workout templates`
					}
				],
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
						linkedItems: [],
						data: {
							clientData: undefined,
							workout: undefined,
							isWorkoutDesigner: false,
							workoutType: 'workout',
							sessionForDate: undefined
						}
					};

					return sharedData;
				},
				uniqueId: `newWorkout`,
				component: WorkoutFormComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `New`
					};
					return metadata;
				}
			}
		}
	},
	activities: {
		mainPath: 'trainer',
		uniqueId: 'activities',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			custom: {
				uniqueId: `customActivities`,
				component: ListWorkoutsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Activities`
					};
					return metadata;
				},
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IWorkoutListRouteData> = {
						linkedItems: [],
						data: {
							workoutType: 'activity'
						}
					};

					return sharedData;
				}
			},
			new: {
				parentRoutes: (context) => [
					{
						routerLink: context.dependencies.navigateService
							.actionRoute((routes) => routes.workoutRoutes.activities.children.custom)
							.getUrl(),
						title: `Activities`
					}
				],
				standaloneSharedData: (context) => {
					const sharedData: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
						linkedItems: [],
						data: {
							clientData: undefined,
							workout: undefined,
							isWorkoutDesigner: false,
							workoutType: 'activity',
							sessionForDate: undefined
						}
					};

					return sharedData;
				},
				uniqueId: `newActivity`,
				component: WorkoutFormComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `New`
					};
					return metadata;
				}
			}
		}
	},
	workout: {
		uniqueId: 'workout',
		mainPath: 'trainer',
		parentRoutes: (context) => {
			const itemRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.workoutRoutes.workouts.children.custom)
						.getUrl(),
					title: `Workouts templates`
				},
				...itemRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<WorkoutDb.IWorkoutItem>((builder) =>
					builder.withCustomApiRoute('workout', 'getPlan').withId(context.routeData.primaryId).build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
							data: {
								workoutType: 'workout',
								workout: response.data,
								isWorkoutDesigner: false,
								clientData: undefined,
								sessionForDate: undefined
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.workoutRoutes.workout.children.design, context.routeData.primaryId)
					.getUrl(),
				title: `Design`,
				iconType: 'design'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.workoutRoutes.workout.children.view, context.routeData.primaryId)
					.getUrl(),
				title: `Preview`,
				iconType: 'preview'
			}
			// {
			// 	routerLink: context.dependencies.navigateService
			// 		.actionRoute((routes) => routes.workoutRoutes.workout.children.edit, context.routeData.primaryId)
			// 		.getUrl(),
			// 	title: 'Instructions & notes',
			// 	iconType: 'text'
			// }
		],
		children: {
			edit: {
				uniqueId: `editWorkout`,
				component: WorkoutFormComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: 'Instructions & notes',
						card: getWorkoutCard(context.dependencies, context.sharedData?.data?.workout)
					};

					return metadata;
				}
			},
			view: {
				uniqueId: `viewWorkout`,
				component: ViewOrEditWorkoutComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: 'Preview',
						card: getWorkoutCard(context.dependencies, context.sharedData?.data?.workout)
					};

					return metadata;
				},
				processSharedData: (context) => {
					context.sharedData.data.isWorkoutDesigner = false;
					return context.sharedData;
				}
			},
			design: {
				uniqueId: `designWorkout`,
				component: ViewOrEditWorkoutComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: 'Design',
						card: getWorkoutCard(context.dependencies, context.sharedData?.data?.workout)
					};

					return metadata;
				},
				processSharedData: (context) => {
					context.sharedData.data.isWorkoutDesigner = true;
					return context.sharedData;
				}
			}
		}
	},
	activity: {
		uniqueId: 'activity',
		mainPath: 'trainer',
		parentRoutes: (context) => {
			const itemRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.workoutRoutes.activities.children.custom)
						.getUrl(),
					title: `Activities`
				},
				...itemRoutes
			];
		},
		loadSharedData: (context) => {
			if (!context.routeData.primaryId) {
				throw Error(`Missing primary id for activity`);
			}
			return context.dependencies.httpService
				.getSingle<WorkoutDb.IWorkoutItem>('workout', context.routeData.primaryId)
				.pipe(
					map((response) => {
						const shared: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
							data: {
								workoutType: 'activity',
								workout: response.data,
								isWorkoutDesigner: false,
								clientData: undefined,
								sessionForDate: undefined
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute((routes) => routes.workoutRoutes.activity.children.edit, context.routeData.primaryId)
					.getUrl(),
				title: `Edit`,
				iconType: 'edit'
			}
		],
		children: {
			edit: {
				uniqueId: `editActivity`,
				component: WorkoutFormComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: 'Edit',
						card: getActivityCard(context.dependencies, context.sharedData?.data?.workout)
					};

					return metadata;
				}
			}
		}
	}
};

function getWorkoutCard(
	dependencies: CoreAppComponentDependencies,
	workout: WorkoutDb.IWorkoutItem | undefined
): ITypePageCard | undefined {
	if (!workout) {
		return undefined;
	}

	const pdfButton: IButton | undefined =
		workout && workout.type === 'workout'
			? {
					title: 'Download workout PDF',
					click: () => {
						dependencies.dialogService.openPdfDialog({
							generatePdf: () => {
								return dependencies.httpService.generateWorkoutPdf({
									workout: workout,
									userId: undefined,
									containerItemId: undefined
								});
							}
						});
					}
			  }
			: undefined;

	const pageTags: IPageTag[] = [
		{
			icon: 'dumbbell',
			text: pluralHelper.getPluralText({
				count: workout.exerciseContainers.length,
				m0: `0 exercises`,
				m1: `1 exercise`,
				m5: `${workout.exerciseContainers.length} exercises`
			})
		}
	];

	if (workout.recommendedLength) {
		pageTags.push({
			icon: 'length',
			text: workout.recommendedLength
		});
	}

	return {
		title: dependencies.coreAppService.getTitleCardConfig<IViewOrEditWorkoutPlanRouteData>({
			updateSharedData: (title, sharedData) => {
				if (sharedData.data?.workout) {
					sharedData.data.workout.system.name = title;
					sharedData.data.workout.system.updated = dependencies.timeService.nowCosmosDb();
				}
				return sharedData;
			},
			item: workout
		}),
		updated: workout.system.updated,
		avatar: {
			avatarName: workout.system.name,
			avatar: workout.avatar?.[0]?.image,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<
				WorkoutDb.IWorkoutItem,
				IViewOrEditWorkoutPlanRouteData
			>({
				type: 'workout',
				existingAvatar: workout?.avatar?.[0],
				patchModel: (file) => {
					return {
						id: workout.id,
						avatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data.workout) {
						sharedData.data.workout.avatar = file ? [file] : [];
						sharedData.data.workout.system.updated = dependencies.timeService.nowCosmosDb();
					}

					return sharedData;
				}
			})
		},
		tags: pageTags,
		buttons: [
			...(pdfButton ? [pdfButton] : []),
			{
				title: 'More',
				nestedButtons: [
					{
						title: 'Delete',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to delete '${workout.system.name}'?`
						},
						clickAsync: () => {
							return dependencies.httpService.deleteSingle('workout', workout.id).pipe(
								map((response) => {
									dependencies.notificationService.notifyDeleted('Workout');
									dependencies.navigateService
										.actionRoute((appRoutes) => appRoutes.workoutRoutes.workouts.children.custom)
										.navigate();
								})
							);
						}
					}
				]
			}
		],
		layoutStyle: 'default'
	};
}

function getActivityCard(
	dependencies: CoreAppComponentDependencies,
	workout: WorkoutDb.IWorkoutItem | undefined
): ITypePageCard | undefined {
	if (!workout) {
		return undefined;
	}

	return {
		title: dependencies.coreAppService.getTitleCardConfig<IViewOrEditWorkoutPlanRouteData>({
			updateSharedData: (title, sharedData) => {
				if (sharedData.data?.workout) {
					sharedData.data.workout.system.name = title;
					sharedData.data.workout.system.updated = dependencies.timeService.nowCosmosDb();
				}
				return sharedData;
			},
			item: workout
		}),
		avatar: {
			avatarName: workout.system.name,
			avatar: workout?.avatar?.[0]?.image,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<
				WorkoutDb.IWorkoutItem,
				IViewOrEditWorkoutPlanRouteData
			>({
				type: 'workout',
				existingAvatar: workout?.avatar?.[0],
				patchModel: (file) => {
					return {
						id: workout.id,
						avatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data.workout) {
						sharedData.data.workout.avatar = file ? [file] : [];
						sharedData.data.workout.system.updated = dependencies.timeService.nowCosmosDb();
					}

					return sharedData;
				}
			})
		},
		tags: [
			{
				icon: workout.iconType ?? 'workoutActivity',
				text: 'Activity'
			},
			{
				icon: 'calendar',
				text: `Updated ${dependencies.timeService.fromNow(workout.system.updated)}`
			}
		],
		buttons: [
			{
				title: 'More',
				nestedButtons: [
					{
						title: 'Delete',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to delete '${workout.system.name}'?`
						},
						clickAsync: () => {
							return dependencies.httpService.deleteSingle('workout', workout.id).pipe(
								map((response) => {
									dependencies.notificationService.notifyDeleted('Activity');

									dependencies.navigateService
										.actionRoute((appRoutes) => appRoutes.workoutRoutes.activities.children.custom)
										.navigate();
								})
							);
						}
					}
				]
			}
		],
		layoutStyle: 'default'
	};
}
